// Architecture data model + validation rules for the Output page.
// All hierarchy validation flows through canNest(). Drive it from data, never
// from scattered if/else branches.

/**
 * @typedef {'aws'|'azure'|'gcp'} Provider
 * @typedef {'structural'|'resource'} NodeKind
 *
 * @typedef {Object} ArchNode
 * @property {string} id
 * @property {string} type
 * @property {NodeKind} kind
 * @property {string} name
 * @property {string|null} parentId
 * @property {{ region?:string, tier?:string, redundancy?:string, estMonthlyCost?:number }} config
 * @property {string} description
 * @property {string} reasoning
 * @property {string} [docsUrl]
 *
 * @typedef {Object} PeerConnection
 * @property {string} id
 * @property {string} sourceId
 * @property {string} targetId
 * @property {string} label
 */

// Ordered structural chain per provider (top -> bottom). A structural node may
// only be a child of the structural type immediately above it, unless it's in
// SELF_NESTABLE for that provider.
export const STRUCTURAL_CHAIN = {
  aws: ["account", "vpc", "subnet"], // public-subnet | private-subnet normalize to subnet
  azure: ["tenant", "management-group", "subscription", "resource-group", "virtual-network", "subnet"],
  gcp: ["organization", "folder", "project", "vpc-network", "subnet"],
};

// Subnet type variants that all normalize to the "subnet" chain level, so a tree
// can express a public/private split without either rule set needing a separate
// level per variant. Mirror of the same list in backend/hierarchy_validation.py.
export const SUBNET_ALIASES = ["public-subnet", "private-subnet"];

// Structural types that may nest within themselves (org-style hierarchies).
export const SELF_NESTABLE = {
  aws: [],
  azure: ["management-group"],
  gcp: ["folder"],
};

// Optional structural parents — legal parents in addition to the immediately
// preceding chain slot. Reflects real cloud reality (GCP folders are optional
// so `project` may attach directly to `organization`). Mirror of the same
// map in /app/backend/hierarchy_validation.py.
export const OPTIONAL_STRUCTURAL_PARENTS = {
  gcp: { project: ["organization"] },
  azure: {},
  aws: {},
};

// The structural level(s) that RESOURCES may attach to.
// NOTE: every list keeps the provider's pre-network-levels parent ("resource-group"
// for azure, "account"/"project" elsewhere). That is deliberate and load-bearing:
// hierarchies persisted before virtual-network/subnet existed parent their resources
// straight to that level, and must keep validating.
export const RESOURCE_PARENTS = {
  aws: ["account", "vpc", "subnet"], // global services -> account; networked -> subnet
  azure: ["resource-group", "virtual-network", "subnet"],
  gcp: ["project", "vpc-network", "subnet"],
};

// Per-tier allow-list of parent types. Mirrors the auto-seed mapping in
// _seed_hierarchy_from_flat (backend) so the picker offers the same shape
// the generator produces. Used by canNest's resource branch when the caller
// passes a tier name as childType (the typical call pattern in this app).
export const RESOURCE_TIER_PARENTS = {
  aws: {
    compute: ["subnet"],
    data: ["subnet"],
    networking: ["subnet", "account", "vpc"], // ALB → subnet; Route53/CloudFront → account; IGW/route table → vpc
    frontend: ["account", "subnet"],          // CloudFront, S3 static → account
    security: ["account", "subnet", "vpc"],   // IAM, KMS → account; security groups → vpc
  },
  azure: {
    compute: ["resource-group", "subnet"],
    data: ["resource-group", "subnet"],
    networking: ["resource-group", "subnet", "virtual-network"],
    frontend: ["resource-group"],
    security: ["resource-group", "virtual-network"], // NSGs → vnet
  },
  gcp: {
    compute: ["subnet"],
    data: ["subnet", "project"],
    networking: ["subnet", "project", "vpc-network"],
    frontend: ["project"],
    security: ["project", "vpc-network"], // firewall rules → vpc-network
  },
};

const RESOURCE_TIER_NAMES = new Set([
  "compute",
  "data",
  "networking",
  "frontend",
  "security",
]);

// Normalize subnet variants (public-subnet / private-subnet) to the level "subnet"
// used in STRUCTURAL_CHAIN and RESOURCE_PARENTS. Applies to every provider whose
// chain has a subnet level — mirror of _normalize_type in hierarchy_validation.py.
function normalizeType(provider, type) {
  if (SUBNET_ALIASES.includes(type)) return "subnet";
  return type;
}

// The set of structural types that exist in the chain for quick membership check.
const STRUCTURAL_SET = Object.fromEntries(
  Object.entries(STRUCTURAL_CHAIN).map(([p, chain]) => [
    p,
    new Set([...chain, ...(chain.includes("subnet") ? SUBNET_ALIASES : [])]),
  ])
);

export function isStructuralType(provider, type) {
  return STRUCTURAL_SET[provider]?.has(type) ?? false;
}

/**
 * Returns true if a node of childType can be placed directly under a node of parentType.
 *
 * - structural -> structural: childType immediately follows parentType in the
 *   chain, OR self-nesting is allowed for that type.
 * - resource -> structural: parentType (normalized) is in RESOURCE_PARENTS.
 *
 * @param {Provider} provider
 * @param {string} childType
 * @param {string} parentType
 * @param {NodeKind} [childKind]
 */
export function canNest(provider, childType, parentType, childKind) {
  if (!provider || !childType || !parentType) return false;
  const chain = STRUCTURAL_CHAIN[provider] || [];
  const normChild = normalizeType(provider, childType);
  const normParent = normalizeType(provider, parentType);
  const kind = childKind || (isStructuralType(provider, childType) ? "structural" : "resource");

  if (kind === "structural") {
    // Self-nesting allowed?
    if (normChild === normParent && (SELF_NESTABLE[provider] || []).includes(normChild)) {
      return true;
    }
    // Optional-skip parents (e.g. GCP `project` may nest directly under
    // `organization` when folders aren't used).
    const optional =
      (OPTIONAL_STRUCTURAL_PARENTS[provider] || {})[normChild] || [];
    if (optional.includes(normParent)) return true;
    const childIdx = chain.indexOf(normChild);
    const parentIdx = chain.indexOf(normParent);
    if (childIdx <= 0 || parentIdx < 0) return false;
    return parentIdx === childIdx - 1;
  }

  // Resource branch.
  // When the caller passes a tier name as childType (the typical path from
  // the picker / sidebar drag — they categorize by tier, not by service slug),
  // use the per-tier matrix for proper provider-aware gating.
  if (RESOURCE_TIER_NAMES.has(normChild)) {
    const allowed = (RESOURCE_TIER_PARENTS[provider] || {})[normChild] || [];
    return allowed.includes(normParent);
  }
  // Fallback: any RESOURCE_PARENTS membership (childType is an arbitrary slug).
  return (RESOURCE_PARENTS[provider] || []).includes(normParent);
}

// Visual / display metadata for a node type. Resources fall back to generic.
const STRUCTURAL_META = {
  // AWS
  account: { label: "AWS Account", color: "#FF9900" },
  vpc: { label: "VPC", color: "#0EA5E9" },
  subnet: { label: "Subnet", color: "#0EA5E9" },
  "public-subnet": { label: "Public Subnet", color: "#22C55E" },
  "private-subnet": { label: "Private Subnet", color: "#F97316" },
  // Azure
  tenant: { label: "Azure AD Tenant", color: "#3B82F6" },
  "management-group": { label: "Management Group", color: "#6366F1" },
  subscription: { label: "Subscription", color: "#8B5CF6" },
  "resource-group": { label: "Resource Group", color: "#A855F7" },
  "virtual-network": { label: "Virtual Network", color: "#0EA5E9" },
  // GCP
  organization: { label: "Organization", color: "#34A853" },
  folder: { label: "Folder", color: "#FBBC04" },
  project: { label: "Project", color: "#4285F4" },
  "vpc-network": { label: "VPC Network", color: "#0EA5E9" },
};

export function getStructuralMeta(type) {
  return STRUCTURAL_META[type] || { label: type, color: "#64748B" };
}

export const PROVIDER_LABELS = {
  aws: "Amazon Web Services",
  azure: "Microsoft Azure",
  gcp: "Google Cloud",
};

export const PROVIDER_ACCENTS = {
  aws: "#FF9900",
  azure: "#0078D4",
  gcp: "#4285F4",
};

/** Build a parent->children map for fast tree traversal. */
export function indexChildren(nodes) {
  const m = new Map();
  for (const n of nodes) {
    const k = n.parentId ?? "__ROOT__";
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(n);
  }
  return m;
}

/** Collect every descendant id of a given node (excluding the node itself). */
export function descendantIds(nodes, rootId) {
  const map = indexChildren(nodes);
  const out = [];
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop();
    const kids = map.get(cur) || [];
    for (const k of kids) {
      out.push(k.id);
      stack.push(k.id);
    }
  }
  return out;
}
